import { DialogActions, DialogContent, FormControl, FormGroup, Grid, TextField } from "@mui/material";
import DialogFooterWithLoading from "../../Loading/DialogFooterWithLoading";
import HESPFormLabel from "../../HESPFormLabel/HESPFormLabel";
import DialogHESP from "../../DialogHESP/DialogHESP";
import { useEffect, useState } from "react";
import SwitchWithTitle from "../../SwitchWithTitle/SwitchWithTitle";
import { useMutation } from "react-apollo";
import { GET_CHANNEL_QUERY, UPDATE_CHANNEL_MUTATION } from "../Overview/channelQueriesMutations";
import { UpdateChannelMutation, UpdateChannelMutationVariables } from "../Overview/__generated__/UpdateChannelMutation";
import { toast } from "react-toastify";
import THEOadsAssetKeys, { THEOadsAssetKey } from "../THEOadsAssetKeys";

interface Props {
    channelId: string;
    organizationId: string;
    theoads: {
        enabled: boolean;
        assetKeys: THEOadsAssetKey[];
        podDuration: number;
    };
    open: boolean;
    onClose: () => void;
    disableAssetKey?: boolean;
}

export default function ChannelTHEOadsDialog({
    onClose,
    open,
    theoads,
    channelId,
    organizationId,
    disableAssetKey,
}: Props): JSX.Element {
    const [enabled, setEnabled] = useState<boolean>(theoads.enabled);
    const [assetKeys, setAssetKeys] = useState<THEOadsAssetKey[]>(theoads.assetKeys);
    const [podDuration, setPodDuration] = useState<number>(theoads.podDuration);

    useEffect(() => {
        setEnabled(theoads.enabled);
        setAssetKeys(theoads.assetKeys);
        setPodDuration(theoads.podDuration);
    }, [open, theoads]);

    const [updateChannel, { loading }] = useMutation<UpdateChannelMutation, UpdateChannelMutationVariables>(
        UPDATE_CHANNEL_MUTATION,
    );

    async function onSave() {
        try {
            await updateChannel({
                variables: {
                    input: {
                        channelId,
                        organizationId,
                        theoads: {
                            podDuration,
                            assetKeys: assetKeys.map(({ tempId, ...rest }) => rest),
                            enabled,
                        },
                    },
                },
                refetchQueries: [
                    {
                        query: GET_CHANNEL_QUERY,
                        variables: {
                            channelId,
                            organizationId,
                        },
                    },
                ],
            });
            toast.success("THEOads settings updated!");
            onClose();
        } catch {
            toast.error("Something went wrong");
        }
    }

    return (
        <DialogHESP
            title={"Update THEOads settings"}
            open={open}
            onClose={onClose}
            content={
                <>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="THEOads integration" />
                                    <SwitchWithTitle
                                        checked={enabled}
                                        onChange={() => setEnabled(!enabled)}
                                        title="Enable THEOads for this channel"
                                    />
                                </FormControl>
                            </Grid>
                            {enabled && (
                                <>
                                    {" "}
                                    <Grid item xs={12}>
                                        <FormGroup>
                                            <HESPFormLabel label="Pod duration" />
                                            <TextField
                                                fullWidth
                                                type="number"
                                                size="small"
                                                onChange={(e) => setPodDuration(parseInt(e.target.value))}
                                                variant="outlined"
                                                value={podDuration}
                                                InputProps={{
                                                    inputProps: { min: 0, max: 1_000, step: 1 },
                                                    style: {
                                                        minWidth: "80px",
                                                        fontSize: "14px",
                                                        padding: "0px 10px",
                                                        height: "43px",
                                                    },
                                                }}
                                            />
                                        </FormGroup>
                                    </Grid>
                                    {!theoads.enabled && (
                                        <Grid item xs={12}>
                                            {/* only allow to update asset keys when THEOads was disabled, and becomes enabled now */}
                                            <THEOadsAssetKeys
                                                assetKeys={assetKeys}
                                                onChangeKeys={(assetKeys: THEOadsAssetKey[]) => setAssetKeys(assetKeys)}
                                            />
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <DialogFooterWithLoading
                            isLoading={loading}
                            onCancelClick={onClose}
                            onActionClick={onSave}
                            defaultText={"Save"}
                            loadingText={"Saving..."}
                        />
                    </DialogActions>
                </>
            }
        />
    );
}
